import React, { useEffect, useState, useRef } from 'react';
import { FileTextIcon, ClipboardCheckIcon, DollarSignIcon, CheckCircleIcon, ArrowRightIcon, ArrowLeftIcon, ClockIcon, CheckIcon } from 'lucide-react';
export const Timeline = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [animating, setAnimating] = useState(false);
  const [autoplay, setAutoplay] = useState(true);
  const [initialRender, setInitialRender] = useState(true);
  const stepsRef = useRef<(HTMLDivElement | null)[]>([]);
  const steps = [{
    id: 1,
    title: 'Submit Your Request',
    description: 'Fill out our simple form with your deal details',
    icon: <FileTextIcon className="h-6 w-6" data-id="element-525" />,
    timeframe: '5 minutes',
    details: 'Fill out our simple form and our funding specialist will contact you, explaining everything and answering all your questions.',
    tips: ['Include property address and purchase price', 'Specify whether you need EMD or double closing', 'Provide your expected closing timeline']
  }, {
    id: 2,
    title: 'Deal Review & Agreement',
    description: 'We review your deal and prepare the necessary agreements',
    icon: <ClipboardCheckIcon className="h-6 w-6" data-id="element-526" />,
    timeframe: '1-2 hours',
    details: 'After signing the EMD and JV agreement, our Transactional Coordinator team will underwrite your deal and review your documents before authorizing funding.',
    tips: ['No credit checks required', 'Simple paperwork process', 'Clear explanation of terms and fees']
  }, {
    id: 3,
    title: 'Funding Approval',
    description: 'We wire funds directly to the title company',
    icon: <DollarSignIcon className="h-6 w-6" data-id="element-527" />,
    timeframe: 'Same day',
    details: 'The same day we approve the paperwork, we wire the funds you need for your deal straight to the Escrow officer or Title Company/Attorney.',
    tips: ['Funds wired directly to escrow', 'Same-day processing available', 'No delays in your closing timeline']
  }, {
    id: 4,
    title: 'Deal Closing',
    description: 'Your transaction closes and we receive our agreed payment',
    icon: <CheckCircleIcon className="h-6 w-6" data-id="element-528" />,
    timeframe: 'As scheduled',
    details: 'We monitor progress during closing and inspection period, and once the deal closes, we get back the Earnest Money plus the upfront amount agreed.',
    tips: ['No repayment until closing', 'We only get paid when you get paid', 'Support available throughout the process']
  }];
  useEffect(() => {
    if (!autoplay) return;
    const interval = setInterval(() => {
      setAnimating(true);
      setTimeout(() => {
        setActiveStep(prev => (prev + 1) % steps.length);
        setAnimating(false);
      }, 500);
    }, 8000);
    return () => clearInterval(interval);
  }, [autoplay, steps.length]);
  useEffect(() => {
    // Set initialRender to false after the component mounts
    setInitialRender(false);
  }, []);
  useEffect(() => {
    // Skip scrolling on the initial render to prevent scrolling on page load
    if (initialRender) return;
    // Scroll to active step on mobile only after user interaction
    if (window.innerWidth < 768 && stepsRef.current[activeStep]) {
      stepsRef.current[activeStep]?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'start'
      });
    }
  }, [activeStep, initialRender]);
  const handleStepClick = (index: number) => {
    if (index === activeStep) return;
    setAutoplay(false);
    setAnimating(true);
    setTimeout(() => {
      setActiveStep(index);
      setAnimating(false);
    }, 300);
  };
  return <section id="process" className="bg-[#f2efeb] py-14 md:py-20 border-t border-[#d9d3cb]" data-id="element-529">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-530">
        <div className="text-center mb-16 lg:mb-24" data-id="element-531">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6 font-serif-display text-[#2b2826]" data-id="element-532">
            How It <span className="text-[#c9a875]" data-id="element-533">Works</span>
          </h2>
          <p className="text-lg sm:text-xl text-[#2b2826]/80 max-w-3xl mx-auto" data-id="element-534">
            We work to remove any stress or hassle for wholesalers through our
            fast, transparent, reliable, and straightforward transactional
            lending process.
          </p>
        </div>

        {/* Desktop Process Flow */}
        <div className="hidden md:block relative mb-12 md:mb-16" data-id="element-535">
          {/* Progress Bar */}
          <div className="absolute top-[70px] left-0 right-0 h-[1px] bg-[#d9d3cb]" data-id="element-536">
            <div className="h-full bg-[#c9a875] transition-all duration-500" style={{
            width: `${(activeStep + 1) / steps.length * 100}%`
          }} data-id="element-537" />
          </div>

          {/* Step Indicators */}
          <div className="flex justify-between relative z-10" data-id="element-538">
            {steps.map((step, index) => <div key={index} className={`flex flex-col items-center relative z-10 group cursor-pointer` + (index === activeStep ? ' active' : '')} onClick={() => setActiveStep(index)} data-id="element-539">
                <div className={`w-[100px] h-[100px] lg:w-[140px] lg:h-[140px] rounded-full flex items-center justify-center mb-4 lg:mb-6 transition-all duration-500 bg-[#f2efeb] border ${index === activeStep ? 'border-[#c9a875]' : 'border-[#d9d3cb]'} group-hover:border-[#c9a875]/50`} data-id="element-540">
                  <div className={`w-16 h-16 lg:w-24 lg:h-24 rounded-full flex items-center justify-center transition-all duration-300 ${index <= activeStep ? 'bg-[#c9a875] text-white' : 'bg-[#d9d3cb] text-[#2b2826]'} ${index === activeStep ? 'scale-110' : ''}`} data-id="element-541">
                    {step.icon}
                  </div>
                </div>
                <h3 className={`text-lg lg:text-xl font-bold text-center transition-colors ${index === activeStep ? 'text-[#c9a875]' : 'text-[#2b2826]'}`} data-id="element-542">
                  {step.title}
                </h3>
              </div>)}
          </div>
        </div>

        {/* Mobile Step Indicators */}
        <div className="md:hidden flex justify-between mb-6 px-2 overflow-x-auto scrollbar-hide" data-id="element-543">
          {steps.map((step, index) => <div key={index} className="flex flex-col items-center text-center cursor-pointer" onClick={() => setActiveStep(index)} data-id="element-544">
              <div className={`w-full aspect-square rounded-full flex items-center justify-center mb-2 ${index <= activeStep ? 'bg-[#c9a875] text-white' : 'bg-[#d9d3cb] text-[#2b2826]'}`} data-id="element-545">
                {step.icon}
              </div>
              <span className={`text-xs font-bold ${index === activeStep ? 'text-[#c9a875]' : 'text-[#2b2826]'}`} data-id="element-546">
                Step {index + 1}
              </span>
            </div>)}
        </div>

        {/* Mobile Step Carousel */}
        <div className="md:hidden overflow-x-auto pb-4 flex snap-x snap-mandatory scrollbar-hide -mx-4 px-4" data-id="element-547">
          <div className="flex" data-id="element-548">
            {steps.map((step, index) => <div key={index} ref={el => stepsRef.current[index] = el} className="w-[85vw] max-w-[400px] flex-shrink-0 snap-center pr-4" onClick={() => setActiveStep(index)} data-id="element-549">
                <div className={`bg-[#2b2826] border ${index === activeStep ? 'border-[#c9a875]/50' : 'border-[#403a35]'} rounded-3xl p-5 h-full transition-all duration-300`} data-id="element-550">
                  <div className="flex items-center mb-3" data-id="element-551">
                    <div className={`p-2.5 rounded-full ${index <= activeStep ? 'bg-[#c9a875] text-white' : 'bg-[#403a35] text-[#f2efeb]/70'} mr-4 flex-shrink-0`} data-id="element-552">
                      {step.icon}
                    </div>
                    <h3 className={`font-bold text-lg ${index === activeStep ? 'text-[#c9a875]' : 'text-[#f2efeb]'}`} data-id="element-553">
                      {step.title}
                    </h3>
                  </div>
                  <p className="text-[#f2efeb]/70 text-sm line-clamp-2" data-id="element-554">
                    {step.description}
                  </p>
                </div>
              </div>)}
          </div>
        </div>

        {/* Active Step Details */}
        <div className="mt-8 lg:mt-16 max-w-4xl mx-auto" data-id="element-555">
          <div className={`bg-[#2b2826] border border-[#403a35] rounded-3xl p-6 lg:p-10`} data-id="element-556">
            <div className="flex flex-col md:flex-row gap-8 lg:gap-12 items-center" data-id="element-557">
              <div className="md:w-1/2" data-id="element-558">
                <div className="flex items-center mb-6" data-id="element-559">
                  <div className={`p-4 rounded-full bg-[#c9a875] mr-6 flex-shrink-0 text-white`} data-id="element-560">
                    {steps[activeStep].icon}
                  </div>
                  <h3 className="text-2xl lg:text-3xl font-bold text-[#f2efeb] font-serif-display" data-id="element-561">
                    {steps[activeStep].title}
                  </h3>
                </div>
                <p className="text-[#f2efeb]/80 text-lg mb-8 leading-relaxed" data-id="element-562">
                  {steps[activeStep].description}
                </p>

                <ul className="space-y-4" data-id="element-563">
                  {steps[activeStep].tips.map((tip, idx) => <li key={idx} className="flex items-start text-[#f2efeb]/70" data-id="element-564">
                      <CheckCircleIcon className="w-5 h-5 text-[#c9a875] mr-3 flex-shrink-0 mt-0.5" data-id="element-565" />
                      <span data-id="element-566">{tip}</span>
                    </li>)}
                </ul>
              </div>

              <div className="md:w-1/2 w-full" data-id="element-567">
                {/* Visual representation based on active step */}
                {activeStep === 0 && <div className="bg-[#1c1916] rounded-2xl p-6 border border-[#403a35]" data-id="element-568">
                    <div className="space-y-4" data-id="element-569">
                      <div className="h-10 bg-[#2b2826] rounded-xl border border-[#403a35] flex items-center px-4" data-id="element-570">
                        <div className="w-4 h-4 rounded-full bg-[#c9a875]/20 mr-3" data-id="element-571"></div>
                        <div className="h-2 bg-[#403a35] rounded w-1/3" data-id="element-572"></div>
                      </div>
                      <div className="h-10 bg-[#2b2826] rounded-xl border border-[#403a35] flex items-center px-4" data-id="element-573">
                        <div className="w-4 h-4 rounded-full bg-[#c9a875]/20 mr-3" data-id="element-574"></div>
                        <div className="h-2 bg-[#403a35] rounded w-1/2" data-id="element-575"></div>
                      </div>
                      <div className="h-10 bg-[#2b2826] rounded-xl border border-[#403a35] flex items-center px-4" data-id="element-576">
                        <div className="w-4 h-4 rounded-full bg-[#c9a875]/20 mr-3" data-id="element-577"></div>
                        <div className="h-2 bg-[#403a35] rounded w-1/4" data-id="element-578"></div>
                      </div>
                      <div className="h-12 bg-[#c9a875] rounded-xl mt-6 flex items-center justify-center text-white font-medium text-sm" data-id="element-579">
                        Submit Application
                      </div>
                    </div>
                  </div>}

                {activeStep === 1 && <div className="bg-[#1c1916] rounded-2xl p-6 border border-[#403a35] relative overflow-hidden" data-id="element-580">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-[#c9a875]/5 rounded-full blur-xl" data-id="element-581"></div>
                    <div className="flex items-center justify-between mb-6" data-id="element-582">
                      <div className="text-[#f2efeb] font-medium" data-id="element-583">
                        Deal Review
                      </div>
                      <div className="bg-[#c9a875]/20 text-[#c9a875] text-xs px-2 py-1 rounded" data-id="element-584">
                        In Progress
                      </div>
                    </div>
                    <div className="space-y-4 relative z-10" data-id="element-585">
                      <div className="flex items-center justify-between border-b border-[#403a35] pb-3" data-id="element-586">
                        <span className="text-[#f2efeb]/60 text-sm" data-id="element-587">
                          Property Value
                        </span>
                        <span className="text-[#f2efeb] font-medium" data-id="element-588">
                          $250,000
                        </span>
                      </div>
                      <div className="flex items-center justify-between border-b border-[#403a35] pb-3" data-id="element-589">
                        <span className="text-[#f2efeb]/60 text-sm" data-id="element-590">
                          Purchase Price
                        </span>
                        <span className="text-[#f2efeb] font-medium" data-id="element-591">
                          $180,000
                        </span>
                      </div>
                      <div className="flex items-center justify-between" data-id="element-592">
                        <span className="text-[#f2efeb]/60 text-sm" data-id="element-593">
                          Status
                        </span>
                        <span className="text-[#c9a875] font-medium flex items-center" data-id="element-594">
                          <CheckCircleIcon className="w-4 h-4 mr-1" data-id="element-595" /> Approved
                        </span>
                      </div>
                    </div>
                  </div>}

                {activeStep === 2 && <div className="bg-[#1c1916] rounded-2xl border border-[#403a35] overflow-hidden" data-id="element-596">
                    <div className="bg-[#2b2826] p-4 border-b border-[#403a35] flex items-center justify-between" data-id="element-597">
                      <div className="flex items-center" data-id="element-598">
                        <FileTextIcon className="w-5 h-5 text-[#c9a875] mr-2" data-id="element-599" />
                        <span className="text-[#f2efeb] font-medium" data-id="element-600">
                          Closing Documents
                        </span>
                      </div>
                      <div className="w-8 h-8 rounded-full bg-[#1c1916] flex items-center justify-center border border-[#403a35]" data-id="element-601">
                        <CheckIcon className="w-4 h-4 text-[#c9a875]" data-id="element-602" />
                      </div>
                    </div>
                    <div className="p-6" data-id="element-603">
                      <div className="flex items-center mb-4" data-id="element-604">
                        <div className="w-10 h-10 rounded bg-[#2b2826] border border-[#403a35] flex items-center justify-center mr-4" data-id="element-605">
                          <FileTextIcon className="w-5 h-5 text-[#f2efeb]/50" data-id="element-606" />
                        </div>
                        <div data-id="element-607">
                          <div className="text-[#f2efeb] text-sm font-medium" data-id="element-608">
                            A-B Contract.pdf
                          </div>
                          <div className="text-[#f2efeb]/50 text-xs" data-id="element-609">
                            Signed by all parties
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center" data-id="element-610">
                        <div className="w-10 h-10 rounded bg-[#2b2826] border border-[#403a35] flex items-center justify-center mr-4" data-id="element-611">
                          <FileTextIcon className="w-5 h-5 text-[#f2efeb]/50" data-id="element-612" />
                        </div>
                        <div data-id="element-613">
                          <div className="text-[#f2efeb] text-sm font-medium" data-id="element-614">
                            B-C Contract.pdf
                          </div>
                          <div className="text-[#f2efeb]/50 text-xs" data-id="element-615">
                            Signed by all parties
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-16 text-center" data-id="element-616">
          <a href="#funding-form" className="inline-flex items-center gap-2 lg:gap-3 bg-[#c9a875] text-white font-medium py-3 px-6 lg:py-4 lg:px-8 rounded-full hover:bg-[#b08e5c] transition-all text-sm lg:text-base" data-id="element-617">
            Start Your Application
            <ArrowRightIcon className="w-4 h-4 lg:w-5 lg:h-5" data-id="element-618" />
          </a>
        </div>
      </div>
    </section>;
};